import { jwtDecode } from "jwt-decode"
import moment from "moment"
import { put, select, delay, take } from "redux-saga/effects"
import sleep from "helpers/sleep"
import { Types as HistoryTypes } from "../../actions/app/history"
import { sessionExpired } from "../../actions/user"
import { processResponse } from "./utils"

// Don't wait until the token is almost expired to renew it, otherwise in some cases the call might fail because the token will expire
// between the ui verification and the server.
const EXPIRATION_BUFFER = 5

let tokenPromise = null
let tokenSessionInfo = null
let waitUntilSignIn = false

// possible time delta between client and server local time
// we need to calculate and keep this value to sync server timestamps
let syncDelta = 0

async function getCode(account) {
  return fetch(`/auth/code/${account}`, {
    method: "post",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
    },
  }).then(processResponse)
}

export function init(token) {
  const decodedToken = jwtDecode(token)
  tokenSessionInfo = {
    expiration: decodedToken.exp,
    duration: decodedToken.duration,
    rolling: decodedToken.rolling,
  }
  tokenPromise = Promise.resolve(token)
  syncDelta = +window.localStorage.getItem("ompnt-syncdelta") || 0
}

function redirectToRequestAccessForm(accountId) {
  window.location = `/admin/accounts/${accountId}/request-access?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`
}

async function fetchToken(account, userId) {
  tokenSessionInfo = null
  // Will wait until the sign in is done to return the token promise
  while (waitUntilSignIn) {
    await sleep(200)
  }

  let code
  try {
    const response = await getCode(account)
    code = response.code

    const decodedToken = jwtDecode(code)
    if (decodedToken.accessRequestExpired) {
      redirectToRequestAccessForm(decodedToken.accountId)
    }

    tokenSessionInfo = {
      expiration: decodedToken.exp,
      duration: decodedToken.duration,
      rolling: decodedToken.rolling,
    }

    syncDelta = moment().unix() - decodedToken.iat
    window.localStorage.setItem("ompnt-syncdelta", syncDelta)

    window.localStorage.setItem(`ompnt-account-${userId}-${account}`, code)
    window.localStorage.setItem(`ompnt-session-${userId}`, JSON.stringify({
      lastSignedInAt: decodedToken.lastSignedInAt,
      lastCodeRefresh: decodedToken.lastCodeRefresh,
    }))
  } catch {
    window.location = "/logout"
  }
  return code
}

function isExpired() {
  const currentTime = moment().unix() + EXPIRATION_BUFFER
  const expiration = tokenSessionInfo.expiration + syncDelta
  return currentTime > expiration
}

export function* getToken() {
  // If the promise is under execution but haven't returned yet, do not check expiration but return the promise
  const tokenExpired = tokenSessionInfo && isExpired()
  if (tokenPromise && !tokenExpired) {
    return yield tokenPromise
  }

  const currentTime = moment().unix()

  // eslint-disable-next-line prefer-const
  let { account, accountMeta, user } = yield select(state => ({
    account: state.global.selected.account,
    accountMeta: state.global.selected.accountMeta,
    user: state.user,
  }))

  const storageToken = window.localStorage.getItem(`ompnt-account-${user.id}-${account}`)
  if (storageToken) {
    const decodedStorageToken = jwtDecode(storageToken)
    // If it's the first time use the token, if it's not, only use it if the expiration is greater then the current token
    if (!tokenPromise || (tokenSessionInfo && decodedStorageToken.exp > tokenSessionInfo.expiration)) {
      init(storageToken)
      if (currentTime + EXPIRATION_BUFFER <= decodedStorageToken.exp + syncDelta) {
        // If that token is not expired yet, do not refresh
        return yield tokenPromise
      }
    }
  }

  const sessionStorage = window.localStorage.getItem(`ompnt-session-${user.id}`)
  if (sessionStorage) {
    if (!accountMeta) {
      const action = yield take([HistoryTypes.SELECT_ACCOUNT])
      accountMeta = action.account
    }
    const { lastCodeRefresh, lastSignedInAt } = JSON.parse(sessionStorage)
    const { preventSessionRolling, customSessionDuration } = accountMeta

    if (customSessionDuration && !user.isSuperuser) {
      let verificationTime = lastSignedInAt
      if (!preventSessionRolling && lastCodeRefresh) {
        verificationTime = lastCodeRefresh
      }

      // Check if the current token we have need to be renewed with the user signin in again
      if (!waitUntilSignIn && currentTime - verificationTime + syncDelta > customSessionDuration) {
        waitUntilSignIn = true

        // assign the promise so subsequent call get it
        tokenPromise = fetchToken(account, user.id)
        let loggedIn = false
        yield put(sessionExpired({ sessionExpired: true }))
        do {
          const { sessionExpired } = yield select(state => ({
            sessionExpired: state.user.sessionExpired,
          }))

          yield delay(500)
          loggedIn = !sessionExpired
        } while (!loggedIn)

        waitUntilSignIn = false

        return yield tokenPromise
      }
    }
  }

  // Other queries asked for the token while we are waiting for the user to complete the refresh sign in
  if (!waitUntilSignIn) {
    tokenPromise = fetchToken(account, user.id)
  }

  return yield tokenPromise
}
